import { mock } from '@/common/consts';
import { BaseDataResponse, BaseResponse } from '@/type';
import { BaseModel, MutationResult, restMutation } from 'vue-apollo-model';

interface ContractShowResponse {
    show: boolean; // 是否展示合同管理页
    contractStatus: number; // 合同签署状态：0签署中 1已删除 2已完成 3已撤销 4已拒签 5已过期
    changePhone: boolean; // 更换手机号
    changeAdmin: boolean; // 更换管理员
    perStatus: number; // 个人审核状态：0：未激活；1：未认证；2：审核通过；3：已提交待审核；4：审核不通过
    entStatus: number; // 审核状态：0：未激活；1：未认证；2：已完成企业信息填写；3：企业认证通过；4：企业认证失败审核不通过
}
type ContractShowResponseType = BaseDataResponse<ContractShowResponse>;

interface EnterpriseInfo {
    companyName: string; // 企业名称
    creditNo: string; // 统一社会信用代码
    legalName: string; // 法人
    licensePath: string; // 营业执照 /account/file/img/licensePath
}
type EnterpriseInfoType = BaseDataResponse<EnterpriseInfo>;

interface ContractSignInfo {
    companyRegisteredAddress: string;
    companyMailingAddress: string;
    gameProfitable: boolean;
    invoiceUpdateEnable: boolean;
    invoiceContentType: number;
    invoiceType: number;
}
type ContractSignInfoType = BaseDataResponse<ContractSignInfo>;

export interface ContractSignUrlSubmit {
    appId: string;
    originalContractNo: string;
    returnUrl: string;
    companyRegisteredAddress: string;
    companyMailingAddress: string;
    invoiceContentType: number | string;
    invoiceType: number | string;
    iapInvoiceContentType: number | string;
    iapInvoiceType: number | string;
}
interface ContractSignUrl {
    contractNo: string;
    signUrl: string;
}
type ContractSignUrlType = BaseDataResponse<ContractSignUrl>;

interface ContractSignStatus {
    contractStatus: number;
    gameName: string;
}
type ContractSignStatusType = BaseDataResponse<ContractSignStatus>;

interface ContractDetailInfo {
    contractInfo: {
        contractNo: string;
        contractName: string;
        firstParty: string;
        secondParty: string;
        startDate: string;
        endDate: string;
        contractType: number;
    };
    bankInfo: {
        bankCardName: string;
        bankCardNo: string;
        bankBranch: string;
        bankName: string;
        bankCity: string;
    };
    notifyRenewContract: boolean;
}
type ContractDetailInfoType = BaseDataResponse<ContractDetailInfo>;

export class GameContractModel extends BaseModel {
    // 用于判断合同管理页显示状态
    // https://kapi.corp.kuaishou.com/project/8039/interface/api/835122
    @restMutation<GameContractModel>({
        url: '/portal/app/miniGame/contract/showpage',
        variables: (p) => p,
        headers: {
            'content-type': 'application/x-www-form-urlencoded',
        },
    })
    contractShowMutation!: MutationResult<
        Record<string, string>,
        ContractShowResponseType
    >;
    async getAuth(p: Record<string, string>) {
        await this.contractShowMutation.mutate(p);
    }

    get isShowTab() {
        return this.contractShowMutation.data.data?.show || false;
    }

    get pageStatus() {
        return (
            this.contractShowMutation.data.data || {
                show: false,
                contractStatus: 0,
                perStatus: 0, // 个人审核状态：0：未激活；1：未认证；2：审核通过；3：已提交待审核；4：审核不通过 ；10:提交到电签平台后修改了信息
                entStatus: 0, // 0：未认证；1：管理员资料已提交；2：企业基本资料(没有申请表)已提交；3：已提交待审核；4：审核通过；5：审核不通过；6人工初审通过
                changePhone: false, // 更换手机号
                changeAdmin: false,
            }
        );
    }

    // 企业信息获取
    // https://kapi.corp.kuaishou.com/project/8039/interface/api/835343
    @restMutation<GameContractModel>({
        url: '/portal/app/miniGame/enterpriseInfo',
        method: 'get',
        variables: (p) => p,
        headers: {
            'content-type': 'application/x-www-form-urlencoded',
        },
    })
    enterpriseInfoMutation!: MutationResult<any, EnterpriseInfoType>;

    async enterpriseInfo(p: Record<string, string>) {
        await this.enterpriseInfoMutation.mutate(p);
    }

    get enterprise() {
        return (
            this.enterpriseInfoMutation.data.data ?? {
                companyName: '',
                creditNo: '',
                legalName: '',
                licensePath: '',
            }
        );
    }

    // 提交企业实名认证
    // https://kapi.corp.kuaishou.com/project/8039/interface/api/835130
    @restMutation<GameContractModel>({
        url: '/portal/app/miniGame/ent/realyname',
        method: 'post',
        variables: (p) => p,
        headers: {
            'content-type': 'application/x-www-form-urlencoded',
        },
    })
    entRealynameMutation!: MutationResult<Record<string, any>, BaseResponse>;

    async entRealyname(p: Record<string, any>) {
        await this.entRealynameMutation.mutate(p);
    }

    // 合同签署-基础信息获取
    // https://kapi.corp.kuaishou.com/project/8039/interface/api/840322
    @restMutation<GameContractModel>({
        url: '/portal/app/miniGame/contract/preSign',
        method: 'get',
        variables: (p) => p,
        headers: {
            'content-type': 'application/x-www-form-urlencoded',
        },
    })
    contractSignInfoMutation!: MutationResult<
        Record<string, any>,
        ContractSignInfoType
    >;

    async contractSignInfoMut(p: Record<string, any>) {
        await this.contractSignInfoMutation.mutate(p);
    }
    get contractSignInfo() {
        return (
            this.contractSignInfoMutation.data.data ?? {
                companyRegisteredAddress: '',
                companyMailingAddress: '',
                gameProfitable: false,
                invoiceContentType: 1,
                invoiceType: 1,
                invoiceUpdateEnable: true,
            }
        );
    }

    // 合同签署-发起签署获取电签平台url
    // https://kapi.corp.kuaishou.com/project/8039/interface/api/840385
    @restMutation<GameContractModel>({
        url: '/portal/app/miniGame/contract/sign',
        method: 'post',
        variables: (p) => p,
        headers: {
            'content-type': 'application/x-www-form-urlencoded',
        },
    })
    contractSignUrlMutation!: MutationResult<
        ContractSignUrlSubmit,
        ContractSignUrlType
    >;

    async contractSignUrlMut(p: ContractSignUrlSubmit) {
        await this.contractSignUrlMutation.mutate(p);
    }
    get contractSignUrl() {
        return (
            this.contractSignUrlMutation.data.data ?? {
                contractNo: '',
                signUrl: '',
            }
        );
    }

    // 合同签署-签署状态查询
    // https://kapi.corp.kuaishou.com/project/8039/interface/api/840436
    @restMutation<GameContractModel>({
        url: '/portal/app/miniGame/contract/status',
        method: 'post',
        variables: (p) => p,
        headers: {
            'content-type': 'application/x-www-form-urlencoded',
        },
    })
    contractSignStatusMutation!: MutationResult<
        Record<string, any>,
        ContractSignStatusType
    >;

    async contractSignStatusMut(p: Record<string, any>) {
        await this.contractSignStatusMutation.mutate(p);
    }
    get contractSignStatus() {
        return (
            this.contractSignStatusMutation.data.data || {
                contractStatus: 0,
                gameName: '',
            }
        );
    }

    // 合同详情查询
    // https://kapi.corp.kuaishou.com/project/8039/interface/api/838522
    @restMutation<GameContractModel>({
        url: '/portal/app/miniGame/contract/contractInfo',
        method: 'get',
        variables: (p) => p,
        headers: {
            'content-type': 'application/x-www-form-urlencoded',
        },
    })
    contractDetailInfoMutation!: MutationResult<
        Record<string, any>,
        ContractDetailInfoType
    >;

    async contractDetailInfoMut(p: Record<string, any>) {
        await this.contractDetailInfoMutation.mutate(p);
    }

    get notifyRenewContract() {
        return this.contractDetailInfoMutation.data.data?.notifyRenewContract;
    }

    get contractDetailInfo() {
        return (
            this.contractDetailInfoMutation.data.data ?? {
                contractInfo: {
                    contractNo: '',
                    contractName: '',
                    firstParty: '',
                    secondParty: '',
                    startDate: '',
                    endDate: '',
                    contractType: 0,
                },
                bankInfo: {
                    bankCardName: '',
                    bankCardNo: '',
                    bankBranch: '',
                    bankName: '',
                    bankCity: '',
                },
                notifyRenewContract: false,
            }
        );
    }
}
